import type React from 'react'

/**
 * "Checking your signature status" indicator (GEN-5259).
 *
 * Shown on the order page in the window between a clinician completing the embedded
 * signing ceremony and the poller reading a terminal status from provider-api. In
 * that window the order still projects the pre-sign status (`sent`/`delivered`), so
 * this optimistic cue stands in for the signature badge to make it obvious the page
 * is refreshing rather than still unsigned. `OrderInProgress` owns that decision via
 * `useTrfSigningInProgress`; this component only draws the state.
 *
 * Styled to match `TrfSignaturePreparing` (and thus `TrfSignatureBadge`'s pending
 * tone) so the transition from this cue to the final badge reads as one control
 * settling, not two different elements swapping.
 */

interface TrfSignatureRecordingProps {
  className?: string
}

export const TrfSignatureRecording: React.FC<TrfSignatureRecordingProps> = ({ className = '' }) => {
  return (
    <div
      data-testid="trf-signature-recording"
      role="status"
      aria-live="polite"
      className={`inline-flex items-center gap-2 rounded-full border border-warning-300 dark:border-warning-600 bg-warning-50 dark:bg-gray-900 px-3 py-1 text-text-fg-warning dark:text-warning-100 ${className}`.trim()}
    >
      {/* helix-exception: spinner kept; Flowbite Spinner adds role="status" and a label, an a11y change (F-13) */}
      <svg className="h-4 w-4 animate-spin" fill="none" viewBox="0 0 24 24" aria-hidden="true">
        <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
        <path
          className="opacity-75"
          fill="currentColor"
          d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
        />
      </svg>
      <span className="text-sm font-medium">Checking your signature status</span>
    </div>
  )
}

export default TrfSignatureRecording
